import React, { useEffect, useMemo, useState } from "react";
import { RotateCw } from "lucide-react";
import "../styles/device-access.css";
import { buildCameraRecordingUrl, fetchCameraRecordings, fetchClientCameras } from "../services/camera-access";


function formatDateTime(value) {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleString("uk-UA", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    });
}


function formatSize(value) {
    const bytes = Number(value || 0);
    if (!Number.isFinite(bytes) || bytes <= 0) return "-";
    if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${bytes} B`;
}


const CameraAccess = () => {
    const [cameras, setCameras] = useState([]);
    const [selectedCameraId, setSelectedCameraId] = useState(null);
    const [recordings, setRecordings] = useState([]);
    const [selectedRecordingUrl, setSelectedRecordingUrl] = useState("");
    const [loading, setLoading] = useState(true);
    const [recordingsLoading, setRecordingsLoading] = useState(false);
    const [error, setError] = useState("");

    const selectedCamera = useMemo(
        () => cameras.find((item) => item.id === selectedCameraId) || null,
        [cameras, selectedCameraId]
    );

    useEffect(() => {
        const loadCameras = async () => {
            setLoading(true);
            setError("");
            try {
                const items = await fetchClientCameras();
                setCameras(Array.isArray(items) ? items : []);
                const firstId = Array.isArray(items) && items.length > 0 ? items[0].id : null;
                setSelectedCameraId(firstId);
            } catch (e) {
                setError(e?.message || "Не вдалося завантажити камери");
                setCameras([]);
                setSelectedCameraId(null);
            } finally {
                setLoading(false);
            }
        };
        loadCameras();
    }, []);

    useEffect(() => {
        const loadRecordings = async () => {
            if (!selectedCameraId) {
                setRecordings([]);
                setSelectedRecordingUrl("");
                return;
            }
            setRecordingsLoading(true);
            setError("");
            try {
                const payload = await fetchCameraRecordings(selectedCameraId, 72);
                const items = Array.isArray(payload?.items) ? payload.items : [];
                setRecordings(items);
                setSelectedRecordingUrl(items[0]?.download_url ? buildCameraRecordingUrl(items[0].download_url) : "");
            } catch (e) {
                setError(e?.message || "Не вдалося завантажити записи");
                setRecordings([]);
                setSelectedRecordingUrl("");
            } finally {
                setRecordingsLoading(false);
            }
        };
        loadRecordings();
    }, [selectedCameraId]);

    return (
        <div className="device-access-container camera-access-container">
            <div className="config-header">
                <div className="header-buttons">
                    <button
                        className="add-btn refresh toolbar-icon-btn"
                        type="button"
                        onClick={() => window.location.reload()}
                        disabled={loading || recordingsLoading}
                        title="Оновити"
                        aria-label="Оновити"
                    >
                        <RotateCw size={16} style={{ animation: (loading || recordingsLoading) ? "spin 1s linear infinite" : "none" }} />
                    </button>
                </div>
            </div>
            <div className="header-section">
                <p className="camera-access-subtitle">Доступні лише камери та записи вашої локації. На сервері показуються записи за останні 3 дні.</p>
            </div>

            {error && <div className="camera-access-error">{error}</div>}

            {loading ? (
                <div className="camera-access-empty">Завантаження камер...</div>
            ) : cameras.length === 0 ? (
                <div className="camera-access-empty">Для вашої локації ще не налаштовано жодної камери.</div>
            ) : (
                <div className="camera-access-layout">
                    <aside className="camera-access-sidebar">
                        {cameras.map((camera) => (
                            <button
                                key={camera.id}
                                type="button"
                                className={`camera-list-item ${camera.id === selectedCameraId ? "active" : ""}`}
                                onClick={() => setSelectedCameraId(camera.id)}
                            >
                                <span className="camera-list-title">{camera.title}</span>
                                <span className="camera-list-meta">{camera.location_name || `Локація #${camera.location_id}`}</span>
                            </button>
                        ))}
                    </aside>

                    <section className="camera-access-content">
                        {selectedCamera && (
                            <div className="camera-panel">
                                <div className="camera-panel-head">
                                    <div>
                                        <h3>{selectedCamera.title}</h3>
                                        <p>{selectedCamera.descriptions || "Камера входу/під'їзду"}</p>
                                    </div>
                                    {selectedCamera.live_hls_url ? (
                                        <a
                                            className="camera-live-link"
                                            href={selectedCamera.live_hls_url}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            Онлайн потік
                                        </a>
                                    ) : null}
                                </div>

                                {selectedRecordingUrl ? (
                                    <video className="camera-recording-player" controls preload="metadata" src={selectedRecordingUrl} />
                                ) : (
                                    <div className="camera-access-empty camera-player-empty">
                                        {recordingsLoading ? "Завантаження записів..." : "Немає доступних записів за останні 3 дні."}
                                    </div>
                                )}

                                <div className="camera-recordings-list">
                                    {recordings.map((item) => {
                                        const url = buildCameraRecordingUrl(item.download_url);
                                        const isActive = url === selectedRecordingUrl;
                                        return (
                                            <div key={item.id} className={`camera-recording-item ${isActive ? "active" : ""}`}>
                                                <button type="button" className="camera-recording-open" onClick={() => setSelectedRecordingUrl(url)}>
                                                    <span>{formatDateTime(item.starts_at)}</span>
                                                    <span className="camera-recording-range">до {formatDateTime(item.ends_at)}</span>
                                                </button>
                                                <div className="camera-recording-meta">
                                                    <span>{formatSize(item.file_size_bytes)}</span>
                                                    <a href={url} target="_blank" rel="noreferrer">Відкрити файл</a>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </section>
                </div>
            )}
        </div>
    );
};


export default CameraAccess;